/**
 * <eg-card-video> — drives a muted, looping, controls-free product video inside
 * the product-card carousel (markup: snippets/eg-card-video.liquid, gated by the
 * `product_card_video_autoplay` theme setting).
 *
 * Playback rules:
 *   - Plays only while the card is in (or near) the viewport, via a single shared
 *     IntersectionObserver — efficient on collection pages that render many cards.
 *   - Never autoplays for visitors who prefer reduced motion or have Data Saver
 *     enabled; the poster frame stays visible instead (a graceful fallback that
 *     matches the previous still-image behaviour).
 *   - Pauses while the browser tab is hidden.
 *
 * The <video> is server-rendered WITHOUT an `autoplay` attribute and with
 * `preload="none"`, so nothing downloads until we decide to play. Without JS the
 * poster frame is shown — no regression over the previous behaviour.
 */
(function () {
  const TAG = 'eg-card-video';
  if (customElements.get(TAG)) return;

  /** @type {Set<EgCardVideo>} */
  const instances = new Set();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Whether autoplay is permitted at all right now.
   * @returns {boolean}
   */
  function autoplayAllowed() {
    if (reducedMotion.matches) return false;
    // `navigator.connection` is non-standard (Chromium only); guard its access.
    const connection = /** @type {any} */ (navigator).connection;
    if (connection && connection.saveData) return false;
    return true;
  }

  /** @type {IntersectionObserver | null} */
  let observer = null;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target;
          if (el instanceof EgCardVideo) {
            el.intersecting = entry.isIntersecting;
            el.sync();
          }
        }
      },
      // Start a touch early so the video is already looping by the time the
      // card is fully on screen.
      { rootMargin: '200px 0px', threshold: 0 }
    );
    return observer;
  }

  function syncAll() {
    for (const el of instances) el.sync();
  }

  // Re-evaluate every instance when the global conditions change.
  reducedMotion.addEventListener('change', syncAll);
  document.addEventListener('visibilitychange', syncAll);

  class EgCardVideo extends HTMLElement {
    /** @type {HTMLVideoElement | null} */
    video = null;

    /** @type {boolean} */
    intersecting = false;

    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      // Belt-and-suspenders: guarantee inline, silent, looping playback
      // regardless of how the markup was generated.
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.controls = false;
      if (!this.video.getAttribute('preload')) this.video.preload = 'none';

      instances.add(this);
      getObserver().observe(this);
    }

    disconnectedCallback() {
      observer?.unobserve(this);
      instances.delete(this);
      this.pause();
    }

    /** @returns {boolean} */
    get shouldPlay() {
      return this.intersecting && !document.hidden && autoplayAllowed();
    }

    sync() {
      if (this.shouldPlay) this.play();
      else this.pause();
    }

    play() {
      const video = this.video;
      if (!video) return;
      // play() returns a promise that can reject if playback is interrupted
      // (e.g. the element is removed mid-play); swallow that to avoid noise.
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }

    pause() {
      const video = this.video;
      if (video && !video.paused) video.pause();
    }
  }

  customElements.define(TAG, EgCardVideo);
})();
